/**
 * **调试查看器**（`debug.viewer`）取值：**设计期优先，运行期兜底**。
 *
 * ## 为什么需要这一层（用户报的问题）
 *
 * 用户原话：
 *
 * > 我给 JS 代码节点添加了一个输入数值，添加了一个输入布尔，然后输出连接到
 * > 调试输出节点上没有任何输出，也没有任何报错。
 *
 * 那个节点的显示原来**只看设计期的求值结果**。而 JS 节点（以及事件这类
 * 副作用节点）在设计期**不执行** —— 那是这套东西的底线（"打开文档 ≠ 执行文档"，
 * 见 `docs/js-node.md` §2），不能为了让它显示而破。于是把它们的输出接到
 * 调试查看器上，就永远是空的：看起来像"接线坏了"，其实只是**还没运行**。
 *
 * 所以查看器要能读**两个时刻**的值：
 *
 * ```
 *   设计期求值结果（纯节点）  →  有值就用它（改了图立刻能看到）
 *   上一次运行的结果          →  设计期没值时回落到它，并**标出**"上次运行"
 * ```
 *
 * ⚠️ 标出来是必须的：设计期与运行期是两个时刻，不标的话用户会以为
 * 它就是"现在图上的值" —— 而它**不会**跟着图变。
 *
 * ⚠️ 抽成纯函数（不碰 Vue、不碰宿主）是为了能被单测覆盖：这段"先看哪个来源"
 * 的规矩正是这个节点唯一会出错的地方，而它埋在宿主的一个渲染函数里时
 * 是没法断言的。
 */
import { repeaterCount, repeaterHandle } from './rows'
import type { RepeaterRow } from '../types/node'

export interface DebugValue {
  /** 「监听」行的实例 handle（`watch.0` …） */
  handle: string
  value: unknown
  /** 这个值来自**上一次运行**（设计期这类节点不执行） */
  fromRun: boolean
}

/**
 * 取「监听」行每一路的显示值。
 *
 * ⚠️ `undefined` 一律当"**这一路没有值**"（不占一行），不当"值是 undefined"——
 * 没连线的实例、运行失败的节点、设计期不执行的副作用节点都会走到这一支。
 * 这和整张图的规矩一致：没有值就是没有值，下游回退到自己的内联默认。
 */
export function debugValuesOf(
  row: RepeaterRow,
  design: ReadonlyMap<string, unknown> | undefined,
  run: ReadonlyMap<string, unknown> | undefined,
): DebugValue[] {
  const out: DebugValue[] = []
  const total = repeaterCount(row)
  for (let index = 0; index < total; index += 1) {
    const handle = repeaterHandle(row, index)
    const designValue = design?.get(handle)
    if (designValue !== undefined) {
      out.push({ handle, value: designValue, fromRun: false })
      continue
    }
    const runValue = run?.get(handle)
    if (runValue === undefined) continue
    out.push({ handle, value: runValue, fromRun: true })
  }
  return out
}

/* ══════════════════════════════════════════════════════════════
   同一套规矩，给**包编辑器**用（2027 补）
   ══════════════════════════════════════════════════════════════ */

/** 一个节点的输出表：`handle → 值`（`node/debugView.ts` 与宿主共用同一个形状） */
export type OutputMap = ReadonlyMap<string, ReadonlyMap<string, unknown>>

/**
 * ★★ **把上一次运行的结果变成 `evaluateGraph` 的 `seeds`**（只给"副作用节点"）。
 *
 * ## 为什么光合并输出表不够（我第一版就是那么写的，被这份示例文档当场打回）
 *
 * 合并输出表只修好了**副作用节点自己**那一格（调试查看器要的正是这个）。
 * 但打印包的页面编辑器读的是**组件节点的输出对象** —— 那是组件的求值器
 * 在设计期算出来的（输入的 JS 值当时还不存在 → 回退到内联默认值）。
 * 只把两张表并起来，组件对象仍然是"设计期那一份"：
 *
 * ```text
 *   JS 节点 → 文本组件的 text 输入 → 组件求值器 → 组件对象（页面编辑器读它）
 *                 ↑ 设计期这里是空的                 ↑ 于是这里写的是默认值
 * ```
 *
 * 所以要让运行值**真的流下去**，只能**带着它重算一遍**：
 * `evaluateGraph(..., { seeds })` —— 那个选项本来是循环用来把每轮的
 * `item` / `index` 灌进「循环起始节点」的（同一个语义：**这些节点的值由外面给**）。
 *
 * ⚠️ **只 seed 副作用节点**（有 runner 的类型）。理由和调试查看器一样：
 * 纯节点的设计期值永远跟着当前图走（比上一趟的运行值新），seed 它等于
 * 把预览冻结在"上次运行那一刻" —— 那会让"在页面编辑器里改一个字"
 * 都得先按一次运行才看得见。
 */
export function seedNodesFromRun(
  models: readonly { id: string; type: string }[],
  run: OutputMap,
  isEffectType: (type: string) => boolean,
): Record<string, Record<string, unknown>> {
  const seeds: Record<string, Record<string, unknown>> = {}
  if (run.size === 0) return seeds
  for (const model of models) {
    if (!isEffectType(model.type)) continue
    const values = run.get(model.id)
    if (!values || values.size === 0) continue
    seeds[model.id] = Object.fromEntries(values)
  }
  return seeds
}

/**
 * ## 为什么不"把运行输出并进设计期输出表"（第一版的做法，已删）
 *
 * 那个做法只修好了**副作用节点自己**那一格（调试查看器要的正是这个），
 * 而页面编辑器读的是**组件节点的输出对象** —— 那个对象是组件的求值器
 * 在设计期算出来的（当时 JS 的值还不存在 → 回退到内联默认值）。
 * 把两张表并起来，组件对象仍然是"设计期那一份"：
 *
 * ```text
 *   JS 节点 → 文本组件的 text 输入 → 组件求值器 → 组件对象（页面编辑器读它）
 *                 ↑ 设计期这里是空的                 ↑ 于是这里写的是默认值
 * ```
 *
 * 也就是说：**运行值必须真的流下去**，只能带着它重算一遍。
 * 教训记在这儿，免得下次又有人去写那个"看起来更省事"的并集。
 */

